import React, { useContext, useMemo } from 'react';

import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import Typography from 'kingpin/atoms/Typography';

// The bonus engine freezes a period's data once it's pending sign-off or
// closed, and stops recalculating archived ones. Only these still pick up
// metric and assignment changes.
const OPEN_STATUS_LABELS: Partial<Record<BonusTypes.PeriodStatus, string>> = {
  active: 'In progress',
  'in review': 'In review',
};

// Framed and scrollable, as a program can have several open periods
const FRAME_STYLE: React.CSSProperties = {
  margin: '4px 0',
  padding: '4px 12px',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 8,
  maxHeight: 120,
  overflowY: 'auto',
  textAlign: 'left',
};

// List markers take the list's font, so match it to the Body 12 items
const LIST_STYLE: React.CSSProperties = {
  margin: 0,
  paddingLeft: 20,
  fontSize: 12,
  lineHeight: '21px',
  color: 'var(--color-text-secondary)',
};

const PeriodList = ({ periods }: { periods: BonusTypes.BonusPeriod[] }) => (
  <>
    {periods.map((period, index) => (
      <React.Fragment key={period.id}>
        {index > 0 && ', '}
        {period.label} ({OPEN_STATUS_LABELS[period.status]})
      </React.Fragment>
    ))}
  </>
);

/**
 * Lists the bonus periods a change to a bonus metric (or its assignment) will
 * alter: the open periods of each given program.
 */
const AffectedBonusPeriods = ({
  programs,
}: {
  programs: { id: string; name: string }[];
}) => {
  const { bonusPeriodsLookup } = useContext(DatasetDefinitionsContext);

  const openPeriods = useMemo(
    () =>
      programs.map((program) => ({
        program,
        periods: (bonusPeriodsLookup[program.id] ?? [])
          .filter((p) => OPEN_STATUS_LABELS[p.status] !== undefined)
          .sort((a, b) => a.startDate.localeCompare(b.startDate)),
      })),
    [bonusPeriodsLookup, programs],
  );
  const hasOpenPeriods = openPeriods.some((p) => p.periods.length > 0);
  const isMultiProgram = programs.length > 1;

  return (
    <div data-testid="affected-bonus-periods">
      <div>
        <Typography.Body type="Body 12">
          {hasOpenPeriods
            ? 'Bonus results will change for these open periods:'
            : 'No bonus periods are open, so the change applies from the next period.'}
        </Typography.Body>
      </div>
      {hasOpenPeriods && (
        <div style={FRAME_STYLE}>
          <ul style={LIST_STYLE}>
            {isMultiProgram
              ? openPeriods.map(({ program, periods }) => (
                  <li key={program.id}>
                    <Typography.Body type="Body 12">
                      <b>{program.name}</b>:{' '}
                      {periods.length > 0 ? (
                        <PeriodList periods={periods} />
                      ) : (
                        'none open'
                      )}
                    </Typography.Body>
                  </li>
                ))
              : openPeriods[0].periods.map((period) => (
                  <li key={period.id}>
                    <Typography.Body type="Body 12">
                      <PeriodList periods={[period]} />
                    </Typography.Body>
                  </li>
                ))}
          </ul>
        </div>
      )}
      <div>
        <Typography.Body type="Body 12">
          Periods pending sign-off or closed are frozen and won&apos;t change.
        </Typography.Body>
      </div>
    </div>
  );
};

export default AffectedBonusPeriods;
